import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BookOpen } from "lucide-react";
import { RefreshCw, Search } from "react-feather";
import { Link } from "react-router-dom";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { getBookImage } from "../../../global-components/src/utils/book";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import "./style.css";

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface PublicBook {
    source: "catalog" | "google";
    id: string;
    title: string;
    author: string | null;
    cover_url: string | null;
    price: number;
    currency: string;
    description: string | null;
    created_at: string | null;
}

interface PagedResponse {
    data: PublicBook[];
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
}

type FilterType = "all" | "catalog" | "google";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtCurrency = (v: number, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(v);

// ─── Componente ───────────────────────────────────────────────────────────────

export default function BooksPublicPage() {
    const [books, setBooks]         = useState<PublicBook[]>([]);
    const [loading, setLoading]     = useState(true);
    const [page, setPage]           = useState(1);
    const [lastPage, setLastPage]   = useState(1);
    const [total, setTotal]         = useState(0);
    const [filter, setFilter]       = useState<FilterType>("all");
    const [search, setSearch]       = useState("");
    const [searchInput, setSearchInput] = useState("");

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = async (p: number, f: FilterType, s: string) => {
        setLoading(true);
        try {
            const params: Record<string, string> = { page: String(p) };
            if (f !== "all") params.type = f;
            if (s)           params.search = s;

            const res = await axios.get<PagedResponse>(
                `${READBOOKS_API_URL}/books/publicados`,
                { params }
            );

            setBooks(res.data.data);
            setLastPage(res.data.last_page);
            setTotal(res.data.total);
        } catch {
            // silencia — sem auth necessária, sem setMessage
        } finally {
            setLoading(false);
        }
    };

    // recarga quando filtro ou página mudam
    useEffect(() => { load(page, filter, search); }, [page, filter, search]);

    // debounce do campo de busca
    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            setSearch(value);
        }, 450);
    };

    const handleFilter = (f: FilterType) => {
        setFilter(f);
        setPage(1);
    };

    const bookHref = (book: PublicBook) =>
        book.source === "catalog" ? `/catalogo/${book.id}` : `/google-livros/${book.id}`;

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="bp-wrapper margin-top-menu container">

            {/* ── Cabeçalho ── */}
            <div className="bp-header">
                <div>
                    <h1>Livros publicados</h1>
                    <p>
                        {total} livro{total !== 1 ? "s" : ""} disponíve{total !== 1 ? "is" : "l"}
                    </p>
                </div>
                <button
                    className="btn btn-outline-secondary bp-refresh"
                    onClick={() => load(page, filter, search)}
                >
                    <RefreshCw size={14} /> Atualizar
                </button>
            </div>

            {/* ── Barra de busca + filtros ── */}
            <div className="bp-controls">
                <div className="bp-search-wrap">
                    <Search size={15} className="bp-search-icon" />
                    <input
                        type="text"
                        className="bp-search"
                        placeholder="Buscar por título ou autor…"
                        value={searchInput}
                        onChange={e => handleSearchChange(e.target.value)}
                    />
                </div>

                <div className="bp-filters">
                    {(["all", "catalog", "google"] as FilterType[]).map(f => (
                        <button
                            key={f}
                            className={`bp-filter-btn ${filter === f ? "active" : ""}`}
                            onClick={() => handleFilter(f)}
                        >
                            {f === "all"     && "Todos"}
                            {f === "catalog" && "Obras publicadas"}
                            {f === "google"  && "Google Books"}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Conteúdo ── */}
            {loading ? (
                <div className="bp-grid">
                    {Array.from({ length: 20 }).map((_, i) => (
                        <div key={i} className="bp-card bp-skeleton">
                            <div className="bp-sk-cover" />
                            <div className="bp-sk-body">
                                <div className="bp-sk-line bp-sk-line--title" />
                                <div className="bp-sk-line bp-sk-line--author" />
                                <div className="bp-sk-line bp-sk-line--badge" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : books.length === 0 ? (
                <div className="bp-empty">
                    <BookOpen size={44} />
                    <p>Nenhum livro encontrado</p>
                </div>
            ) : (
                <>
                    <div className="bp-grid">
                            {books.map(book => (
                                <Link
                                    key={`${book.source}-${book.id}`}
                                    to={bookHref(book)}
                                    className="bp-card"
                                >
                                    <div className="bp-cover-wrap">
                                        {book.cover_url ? (
                                            <img
                                                src={getBookImage(book.cover_url)}
                                                alt={book.title}
                                                className="bp-cover"
                                                onError={e => {
                                                    (e.target as HTMLImageElement).src = "/img/book_transparent.png";
                                                }}
                                            />
                                        ) : (
                                            <div className="bp-cover-placeholder">
                                                <BookOpen size={28} />
                                            </div>
                                        )}
                                    </div>
    
                                    <div className="bp-info">
                                        <span className="bp-title">{book.title}</span>
                                        {book.author && (
                                            <span className="bp-author">{book.author}</span>
                                        )}
    
                                        <div className="bp-meta">
                                            <span className={`bp-badge bp-badge--${book.source}`}>
                                                {book.source === "catalog" ? "Plataforma" : "Google Books"}
                                            </span>
                                            {book.source === "catalog" && (
                                                book.price > 0 ? (
                                                    <span className="bp-badge bp-badge--paid">
                                                        {fmtCurrency(book.price, book.currency)}
                                                    </span>
                                                ) : (
                                                    <span className="bp-badge bp-badge--free">Gratuito</span>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                    {/* ── Paginação ── */}
                    {lastPage > 1 && (
                        <div className="bp-pagination">
                            <button
                                className="btn btn-outline-secondary"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                ← Anterior
                            </button>
                            <span className="bp-page-info">
                                Página {page} de {lastPage}
                            </span>
                            <button
                                className="btn btn-outline-secondary"
                                disabled={page >= lastPage}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Próxima →
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
